import { ColumnsType } from "antd/es/table";
import { IImpoundActivity } from "../../../../../../../../../../../types/ImpoundActivity";
import { useCallback, useMemo, useState, MouseEvent } from "react";
import dayjs from "../../../../../../../../../../../configs/dayjs";
import { DATE_FORMAT } from "../../../../../../../../../../../lib/constants";
import { Dropdown, MenuProps, Row, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import { getFormattedCurrency } from "../../../../../../../../../../../lib/currencyFormatter";
import { useDelete } from "./useDelete";
import { useRecalculate } from "./useRecalculate";
import { usePermissions } from "../../../../../../../../../../../lib/hooks/usePermissions";

export const useTableColumns = (reloadData: () => void) => {
  const [openedActivityId, setOpenedActivityId] = useState<string | null>(null);

  const { onDeleteBtnClick } = useDelete(reloadData);

  const { hasAccessByController } = usePermissions("ImpoundActivities");

  const {
    calculateBalances,
    updateImpoundWithBalance,
    updateImpoundWithYTD,
    isDisabled,
  } = useRecalculate(reloadData);

  const onDelete = useCallback(
    (e: MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      onDeleteBtnClick(e.currentTarget.id);
    },
    [onDeleteBtnClick]
  );

  const onEdit = useCallback(
    (e: MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      setOpenedActivityId(e.currentTarget.id);
    },
    [setOpenedActivityId]
  );

  const onCalculateBalances = useCallback(
    (e: MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      calculateBalances(e.currentTarget.id);
    },
    [calculateBalances]
  );

  const onUpdateImpoundWithBalance = useCallback(
    (e: MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      updateImpoundWithBalance(e.currentTarget.id);
    },
    [updateImpoundWithBalance]
  );

  const onUpdateImpoundWithYTD = useCallback(
    (e: MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      updateImpoundWithYTD(e.currentTarget.id);
    },
    [updateImpoundWithYTD]
  );

  const columns: ColumnsType<IImpoundActivity> = useMemo(() => {
    const values: ColumnsType<IImpoundActivity> = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: "14%",
        render: (value: string) => dayjs(value).format(DATE_FORMAT),
      },
      {
        title: "Audit",
        dataIndex: "auditNumber",
        key: "auditNumber",
        width: "13%",
        ellipsis: true,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: "20%",
        align: "right",
        render: (value: number, record: IImpoundActivity) => {
          const { direction } = record;

          const tooltipText = `${direction === 1 ? "Into" : "From"} Impound`;

          return (
            <Row className="justify-space-between align-center">
              <Tooltip
                placement="top"
                title={tooltipText}
                overlayClassName="text-sm"
              >
                {direction === 1 ? (
                  <Row className="align-center justify-center impound impound-into">
                    <ArrowLeftOutlined />
                  </Row>
                ) : (
                  <Row className="align-center justify-center impound impound-from">
                    <ArrowRightOutlined />
                  </Row>
                )}
              </Tooltip>
              <Row className="amount__container justify-end">
                <p className="my-0 text-md elipsis">
                  {getFormattedCurrency(value)}
                </p>
              </Row>
            </Row>
          );
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        width: "29%",
        ellipsis: true,
      },
      {
        title: "Balance",
        dataIndex: "balance",
        key: "balance",
        width: "17%",
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value),
      },
    ];

    const menuKeys: string[] = [];

    hasAccessByController("Modify") && menuKeys.push("1");

    hasAccessByController("CalculateBalanceToLast") && menuKeys.push("3");

    hasAccessByController("UpdateImpoundBalance") && menuKeys.push("4");

    hasAccessByController("UpdateImpoundYTD") && menuKeys.push("5");

    hasAccessByController("Delete") && menuKeys.push("2");

    menuKeys.length > 0 &&
      values.push({
        width: "7%",
        render: (_: any, record: IImpoundActivity) => {
          const { id } = record;
          const items: MenuProps["items"] = [];

          menuKeys.includes("1") &&
            items.push({
              key: "1",
              label: (
                <Row id={id} onClick={onEdit}>
                  Edit Record
                </Row>
              ),
            });

          menuKeys.includes("3") &&
            items.push({
              key: "3",
              label: (
                <Row id={id} onClick={onCalculateBalances}>
                  Calculate Balances to Last Trans
                </Row>
              ),
            });

          menuKeys.includes("4") &&
            items.push({
              key: "4",
              label: (
                <Row id={id} onClick={onUpdateImpoundWithBalance}>
                  Update Impound with Hist Balance
                </Row>
              ),
            });

          menuKeys.includes("5") &&
            items.push({
              key: "5",
              label: (
                <Row id={id} onClick={onUpdateImpoundWithYTD}>
                  Update Impound with YTD In & Out
                </Row>
              ),
            });

          menuKeys.includes("2") &&
            items.push({
              key: "2",
              label: (
                <Row id={id} onClick={onDelete}>
                  Delete Record
                </Row>
              ),
            });

          return (
            <div className="ant-table-actions">
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                disabled={isDisabled}
              >
                <EllipsisOutlined
                  className="ant-table-btn"
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            </div>
          );
        },
      });

    return values;
  }, [
    isDisabled,
    onCalculateBalances,
    onDelete,
    onEdit,
    onUpdateImpoundWithBalance,
    onUpdateImpoundWithYTD,
    hasAccessByController,
  ]);

  return { columns, openedActivityId, setOpenedActivityId };
};
